import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Service {
  id: string;
  title: string;
  category: string;
  problems: string[];
  description: string;
  approach: string;
  duration: string;
  pricing: string;
  image: string;
}

const services: Service[] = [
  {
    id: 'scalp-therapy',
    title: 'Clinical Scalp Therapy',
    category: 'Scalp Health',
    problems: ['Hair thinning', 'Oily scalp', 'Dandruff', 'Scalp sensitivity', 'Hair loss'],
    description: 'Professional scalp analysis and therapeutic treatment protocols designed to address underlying scalp conditions and restore optimal scalp health.',
    approach: 'Comprehensive scalp assessment, customized treatment plan, therapeutic massage techniques, and professional-grade scalp care products.',
    duration: '60-90 minutes',
    pricing: 'From $120',
    image: 'https://public.readdy.ai/ai/img_res/9dc26001596a79a5f4aa2bffa1069e47.jpg'
  },
  {
    id: 'hair-loss-treatment',
    title: 'Hair Loss Management',
    category: 'Scalp Health',
    problems: ['Thinning hair', 'Hair loss', 'Weak hair follicles', 'Reduced hair density'],
    description: 'Specialized treatment program combining scalp therapy, follicle stimulation, and professional-grade products to address hair thinning and promote healthier hair growth.',
    approach: 'Multi-session treatment protocol with scalp analysis, targeted therapy, nutritional guidance, and ongoing monitoring of progress.',
    duration: '90 minutes per session',
    pricing: 'Treatment package consultation required',
    image: 'https://readdy.ai/api/search-image?query=hair%20loss%20treatment%20consultation%20professional%20scalp%20examination%20in%20premium%20minimal%20salon%20with%20soft%20beige%20cream%20neutral%20tones%20clean%20modern%20aesthetic%20expert%20analyzing%20hair%20follicles%20calm%20clinical%20environment%20natural%20light&width=800&height=600&seq=hairloss001&orientation=landscape'
  },
  {
    id: 'precision-cutting',
    title: 'Precision Hair Cutting',
    category: 'Hair Styling',
    problems: ['Unmanageable hair', 'Lack of shape', 'Damaged ends', 'Frizzy texture'],
    description: 'Expert cutting techniques tailored to your hair type, face shape, and lifestyle. Every cut begins with understanding your hair\'s natural behavior and your daily styling needs.',
    approach: 'Detailed consultation, hair texture analysis, precision cutting techniques, and styling guidance for home maintenance.',
    duration: '45-60 minutes',
    pricing: 'From $80',
    image: 'https://readdy.ai/api/search-image?query=precision%20hair%20cutting%20session%20in%20elegant%20minimal%20salon%20with%20soft%20neutral%20beige%20cream%20tones%20professional%20hairstylist%20cutting%20client%20hair%20with%20expert%20technique%20calm%20premium%20atmosphere%20clean%20aesthetic%20natural%20lighting&width=800&height=600&seq=cutting001&orientation=landscape'
  },
  {
    id: 'hair-coloring',
    title: 'Professional Hair Colouring',
    category: 'Hair Styling',
    problems: ['Damaged hair from previous coloring', 'Uneven color', 'Dull hair', 'Gray coverage needs'],
    description: 'Advanced coloring techniques using premium professional products that prioritize hair health. From natural tones to creative colors, every service includes scalp protection and hair conditioning.',
    approach: 'Color consultation, scalp barrier protection, professional-grade color application, and post-color treatment to maintain hair integrity.',
    duration: '2-3 hours',
    pricing: 'From $150',
    image: 'https://readdy.ai/api/search-image?query=professional%20hair%20coloring%20service%20in%20luxury%20minimal%20salon%20with%20soft%20beige%20cream%20neutral%20tones%20expert%20colorist%20applying%20color%20to%20client%20hair%20premium%20calm%20environment%20clean%20modern%20aesthetic%20natural%20lighting&width=800&height=600&seq=color001&orientation=landscape'
  },
  {
    id: 'keratin-smoothing',
    title: 'Keratin Smoothing Treatment',
    category: 'Hair Repair',
    problems: ['Frizzy hair', 'Unmanageable texture', 'Humidity damage', 'Rough hair surface'],
    description: 'Professional keratin treatment that transforms frizzy, unmanageable hair into smooth, healthy-looking hair. Reduces styling time and improves hair manageability for months.',
    approach: 'Deep cleansing, keratin application with heat sealing, and post-treatment care instructions for long-lasting results.',
    duration: '3-4 hours',
    pricing: 'From $280',
    image: 'https://readdy.ai/api/search-image?query=keratin%20smoothing%20treatment%20application%20in%20premium%20minimal%20salon%20with%20soft%20neutral%20beige%20cream%20tones%20professional%20applying%20keratin%20to%20client%20hair%20calm%20luxury%20setting%20clean%20aesthetic%20natural%20lighting&width=800&height=600&seq=keratin001&orientation=landscape'
  },
  {
    id: 'hair-repair',
    title: 'Intensive Hair Repair Treatment',
    category: 'Hair Repair',
    problems: ['Severely damaged hair', 'Chemical damage', 'Heat damage', 'Brittle hair', 'Split ends'],
    description: 'Deep reconstructive treatment using professional-grade products to repair damaged hair structure, restore moisture balance, and rebuild hair strength from within.',
    approach: 'Damage assessment, multi-step repair protocol with protein and moisture treatments, and customized home care plan.',
    duration: '60-75 minutes',
    pricing: 'From $100',
    image: 'https://readdy.ai/api/search-image?query=intensive%20hair%20repair%20treatment%20in%20elegant%20minimal%20salon%20with%20soft%20beige%20cream%20neutral%20tones%20professional%20applying%20deep%20conditioning%20treatment%20to%20damaged%20hair%20premium%20calm%20atmosphere%20clean%20aesthetic%20natural%20light&width=800&height=600&seq=repair001&orientation=landscape'
  }
];

const categories = ['All Services', 'Scalp Health', 'Hair Styling', 'Hair Repair'];

export default function Services() {
  const [selectedCategory, setSelectedCategory] = useState('All Services');
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const filteredServices = selectedCategory === 'All Services' 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  // SEO: Update page title and meta description
  useEffect(() => {
    document.title = 'Services | Muses Hair CWB';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional hair and scalp services including Clinical Scalp Therapy, Hair Loss Management, Precision Cutting, Hair Colouring, Keratin Smoothing, and Intensive Hair Repair treatments. Expert solutions for hair thinning, scalp issues, and damaged hair.');
    }
  }, []);

  // SEO: Add Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${siteUrl}/services#webpage`,
          "url": `${siteUrl}/services`,
          "name": "Services | Muses Hair CWB",
          "description": "Professional hair and scalp services including Clinical Scalp Therapy, Hair Loss Management, Precision Cutting, Hair Colouring, Keratin Smoothing, and Intensive Hair Repair treatments. Expert solutions for hair thinning, scalp issues, and damaged hair.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": `${siteUrl}/#website`,
            "url": siteUrl,
            "name": "Muses Hair CWB"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": siteUrl
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Services",
                "item": `${siteUrl}/services`
              }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/services#service-scalp-therapy`,
          "serviceType": "Clinical Scalp Therapy",
          "provider": {
            "@type": "HairSalon",
            "name": "Muses Hair CWB",
            "url": siteUrl
          },
          "description": "Professional scalp analysis and therapeutic treatment protocols designed to address underlying scalp conditions and restore optimal scalp health.",
          "offers": {
            "@type": "Offer",
            "price": "120",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "minPrice": "120",
              "priceCurrency": "USD"
            }
          }
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/services#service-hair-loss`,
          "serviceType": "Hair Loss Management",
          "provider": {
            "@type": "HairSalon",
            "name": "Muses Hair CWB",
            "url": siteUrl
          },
          "description": "Specialized treatment program combining scalp therapy, follicle stimulation, and professional-grade products to address hair thinning and promote healthier hair growth."
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/services#service-precision-cutting`,
          "serviceType": "Precision Hair Cutting",
          "provider": {
            "@type": "HairSalon",
            "name": "Muses Hair CWB",
            "url": siteUrl
          },
          "description": "Expert cutting techniques tailored to your hair type, face shape, and lifestyle.",
          "offers": {
            "@type": "Offer",
            "price": "80",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "minPrice": "80",
              "priceCurrency": "USD"
            }
          }
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/services#service-hair-coloring`,
          "serviceType": "Professional Hair Colouring",
          "provider": {
            "@type": "HairSalon",
            "name": "Muses Hair CWB",
            "url": siteUrl
          },
          "description": "Advanced coloring techniques using premium professional products that prioritize hair health.",
          "offers": {
            "@type": "Offer",
            "price": "150",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "minPrice": "150",
              "priceCurrency": "USD"
            }
          }
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/services#service-keratin`,
          "serviceType": "Keratin Smoothing Treatment",
          "provider": {
            "@type": "HairSalon",
            "name": "Muses Hair CWB",
            "url": siteUrl
          },
          "description": "Professional keratin treatment that transforms frizzy, unmanageable hair into smooth, healthy-looking hair.",
          "offers": {
            "@type": "Offer",
            "price": "280",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "minPrice": "280",
              "priceCurrency": "USD"
            }
          }
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/services#service-hair-repair`,
          "serviceType": "Intensive Hair Repair Treatment",
          "provider": {
            "@type": "HairSalon",
            "name": "Muses Hair CWB",
            "url": siteUrl
          },
          "description": "Deep reconstructive treatment using professional-grade products to repair damaged hair structure, restore moisture balance, and rebuild hair strength from within.",
          "offers": {
            "@type": "Offer",
            "price": "100",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "minPrice": "100",
              "priceCurrency": "USD"
            }
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    script.id = 'services-schema';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('services-schema');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-5xl md:text-6xl mb-6 text-gray-900">Professional Services</h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Every service begins with understanding your specific hair and scalp concerns. 
            With over 20 years of experience and specialized training in both hair styling and scalp therapy, 
            I provide solutions that address the root cause, not just the symptoms.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedService(service)}
              >
                <div className="relative w-full h-64">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/95 text-gray-900 px-4 py-1.5 rounded-full text-xs font-medium">
                      {service.category}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl mb-3 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{service.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">Addresses:</p>
                    <div className="flex flex-wrap gap-2">
                      {service.problems.map((problem, idx) => (
                        <span key={idx} className="bg-white text-gray-700 px-3 py-1 rounded-full text-xs">
                          {problem}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="text-sm font-medium text-gray-900">{service.duration}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Investment</p>
                      <p className="text-sm font-medium text-gray-900">{service.pricing}</p>
                    </div>
                  </div>

                  <button className="w-full mt-6 bg-gray-900 text-white py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="px-6 py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl mb-6 text-gray-900">Not Sure Which Service You Need?</h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
            Every client is unique. Book a complimentary consultation to discuss your specific concerns 
            and receive a personalized treatment recommendation based on your hair and scalp condition.
          </p>
          <button 
            onClick={() => {
              const bookingSection = document.getElementById('booking-form');
              if (bookingSection) {
                bookingSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-gray-900 text-white px-10 py-4 rounded-lg text-base font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Book Free Consultation
          </button>
        </div>
      </section>

      {/* Service Detail Modal */}
      {selectedService && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedService(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-80">
              <img
                src={selectedService.image}
                alt={selectedService.title}
                className="w-full h-full object-cover object-top"
              />
              <button
                onClick={() => setSelectedService(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-900"></i>
              </button>
            </div>
            
            <div className="p-8 md:p-12">
              <div className="mb-4">
                <span className="bg-gray-100 text-gray-900 px-4 py-1.5 rounded-full text-xs font-medium">
                  {selectedService.category}
                </span>
              </div>
              
              <h2 className="font-serif text-4xl mb-4 text-gray-900">{selectedService.title}</h2>
              <p className="text-gray-600 text-base mb-6 leading-relaxed">{selectedService.description}</p>

              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">This Service Addresses:</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedService.problems.map((problem, idx) => (
                    <span key={idx} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm">
                      {problem}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-6 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Treatment Approach:</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{selectedService.approach}</p>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Duration</p>
                  <p className="text-base font-medium text-gray-900">{selectedService.duration}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Investment</p>
                  <p className="text-base font-medium text-gray-900">{selectedService.pricing}</p>
                </div>
              </div>

              <button 
                onClick={() => {
                  setSelectedService(null);
                  setTimeout(() => {
                    const bookingSection = document.getElementById('booking-form');
                    if (bookingSection) {
                      bookingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 300);
                }}
                className="w-full bg-gray-900 text-white py-4 rounded-lg text-base font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Book This Service
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}